// =============================================================================
// resourceDocs.ts — pure helpers for the Resources document store (db-138)
// =============================================================================
// Small, side-effect-free helpers shared by the Documents admin tab and the
// /resource-doc/:id redirect route. Kept out of the data layer because none of
// this touches Supabase — it's formatting + the one URL shape that must stay
// identical wherever a link is produced or parsed.
// =============================================================================

// The path portion of a document link. ONE definition, so the admin tab's
// "Copy link" and the route that resolves it can never drift apart.
export const RESOURCE_DOC_PATH = "/resource-doc";

// Maximum upload size for a Resources document, in bytes. 5 MB = 5242880.
//
// ⚠️ This MUST match the `resource-docs` bucket's `file_size_limit` (db-173;
// originally 25 MB in db-138, dropped to 5 MB on John's call — finding B14).
// Three places have to agree or they contradict each other: the bucket, the
// screen copy, and this client-side check. The BUCKET is the real enforcement —
// this exists only so an oversized file fails fast with a sentence a person can
// read, instead of a raw storage error after a pointless upload.
export const RESOURCE_DOC_MAX_BYTES = 5 * 1024 * 1024;

// Human-readable form of the cap, for screen copy. Derived from the constant so
// the wording can never drift from the number it describes.
export const RESOURCE_DOC_MAX_LABEL = `${RESOURCE_DOC_MAX_BYTES / (1024 * 1024)} MB`;

// "" when the file is acceptable, otherwise the message to show the user.
// Takes anything with a `size` (a File), so it stays testable without a DOM.
export function resourceDocSizeError(file: { size?: number; name?: string } | null): string {
  if (!file) return "";
  const n = Number(file.size);
  if (!Number.isFinite(n) || n <= RESOURCE_DOC_MAX_BYTES) return "";
  return `That file is ${formatFileSize(n)}. The limit is ${RESOURCE_DOC_MAX_LABEL} — please use a smaller file.`;
}

// The full, pasteable link for a document. `origin` is passed in (rather than
// read off window) so this stays pure and testable.
//
// This is what goes into a Resources page body via the editor's Link button. It
// is deliberately built from the row `id`, NOT the storage path: the id never
// changes, so renaming the document or replacing its file leaves every existing
// link working.
export function resourceDocLink(origin: string, id: string): string {
  const base = String(origin || "").replace(/\/+$/, "");
  return `${base}${RESOURCE_DOC_PATH}/${id}`;
}

// The document id inside a link, or "" if this isn't a document link. Accepts a
// full URL or a bare path, so it works on whatever gets pasted.
//
// Used by the Resources editor's link dialog: when you paste a document link it
// looks the document up and offers its NAME as the link text, which is the whole
// point of storing a display name.
export function parseResourceDocId(url: string): string {
  const m = String(url || "").match(/\/resource-doc\/([^/?#\s]+)/i);
  return m ? m[1] : "";
}

// Human file size for the Documents list. Binary units (KB = 1024) to match what
// Windows reports, so a figure here matches what John sees on disk.
export function formatFileSize(bytes: number | null | undefined): string {
  // Explicit null check first: Number(null) is 0 (not NaN), so a missing size
  // would otherwise render as a confident "0 B".
  if (bytes === null || bytes === undefined) return "";
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// A short type label for the list, derived from the MIME type recorded at upload.
// Falls back to the file extension, then to a generic label — a document must
// never render a blank type cell.
const MIME_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
};

export function docTypeLabel(mimeType: string | null | undefined, fileName = ""): string {
  const known = MIME_LABELS[String(mimeType || "").toLowerCase()];
  if (known) return known;
  const ext = String(fileName).split(".").pop();
  if (ext && ext !== fileName) return ext.toUpperCase();
  return "File";
}

// Can the browser display this inline, or must it be downloaded?
//
// Chrome tries to render whatever a URL serves. A .docx served as its own MIME
// type with no Content-Disposition makes it attempt an inline viewer and fail with
// "Couldn't load plugin" — so everything except PDF is forced to download by
// signing the URL with a download filename. PDFs stay inline because viewing one
// without downloading is genuinely useful.
export function isInlineViewable(mimeType: string | null | undefined): boolean {
  return String(mimeType || "").toLowerCase() === "application/pdf";
}

// The filename the browser should save as: the DISPLAY name plus the stored file's
// extension. So "Uniform Request Form" saves as "Uniform Request Form.docx", not
// the internal "1754...-Uniform_Request_Form.docx".
export function downloadFileName(name: string, filePath: string): string {
  // Strip characters that are illegal in filenames on Windows/macOS.
  const base = String(name || "document")
    .replace(/[/\\:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const tail =
    String(filePath || "")
      .split("/")
      .pop() || "";
  const dot = tail.lastIndexOf(".");
  const ext = dot > 0 ? tail.slice(dot + 1).toLowerCase() : "";
  if (!ext) return base;
  return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
}

// The display name suggested when a file is picked: the file name minus its
// extension, so "Uniform Request Form.pdf" pre-fills as "Uniform Request Form".
export function suggestDocName(fileName: string): string {
  const raw = String(fileName || "").trim();
  const dot = raw.lastIndexOf(".");
  return dot > 0 ? raw.slice(0, dot) : raw;
}
